(function () {
  'use strict';

  const profileStates = new WeakMap();
  const ABORTED = 'AbortError';
  const MUTATION_ABORTED = Symbol('mutation-aborted');

  const asText = (value) => value == null ? '' : String(value);
  const dataOf = (envelope) => envelope && typeof envelope === 'object' && Object.prototype.hasOwnProperty.call(envelope, 'data')
    ? envelope.data
    : null;
  const viewRoot = (root, view) => root?.matches?.(`[data-pc-view="${view}"]`)
    ? root
    : root?.querySelector?.(`[data-pc-view="${view}"]`) || root;
  const closestShell = (root) => root?.closest?.('[data-personal-center]') || root;
  const sameAddressId = (left, right) => asText(left) !== '' && asText(left) === asText(right);
  const foldedText = (value) => asText(value).trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const DEFAULT_PHONE_TERRITORY = Object.freeze({ c: 'MX', n: 'Mexico', p: '52', l: [10], z: [] });
  const PHONE_MIN_TOTAL_DIGITS = 5;
  const PHONE_MAX_TOTAL_DIGITS = 15;
  const territoryNameCache = new Map();
  let territoryPickerId = 0;
  const abortError = () => {
    const error = new Error('Aborted');
    error.name = ABORTED;
    return error;
  };

  function getState(registry, root) {
    let state = registry.get(root);
    if (!state) {
      state = {
        addresses: [],
        customer: null,
        profileWritable: false,
        addressWritable: false,
        lastFocus: null,
        listeners: [],
        mutationOperations: new Map(),
        pendingDeleteAddress: null
      };
      registry.set(root, state);
    }
    return state;
  }

  function clearListeners(state) {
    state.listeners.splice(0).forEach(({ target, type, listener, options }) => target.removeEventListener(type, listener, options));
  }

  function addListener(state, target, type, listener, options) {
    target.addEventListener(type, listener, options);
    state.listeners.push({ target, type, listener, options });
  }

  function setValue(root, selector, value) {
    const control = root.querySelector(selector);
    if (control) control.value = asText(value);
  }

  function phoneValue(record) {
    const native = record?.phoneNumber;
    if (native != null && typeof native !== 'object') return asText(native);
    if (native?.phoneNumber || native?.number) return asText(native.phoneNumber || native.number);
    const legacy = record?.phone;
    if (legacy != null && typeof legacy !== 'object') return asText(legacy);
    return asText(native?.phoneNumber || native?.number || legacy?.phoneNumber || legacy?.number);
  }

  function addressValue(address, key) {
    if (key === 'phone') return phoneValue(address);
    if (key === 'countryCode') return address?.countryCode ?? address?.territoryCode;
    if (key === 'provinceCode') return address?.provinceCode ?? address?.zoneCode;
    return address?.[key];
  }

  function territories() {
    return Array.isArray(window.PersonalCenterTerritories)
      ? window.PersonalCenterTerritories.filter((territory) => /^[A-Z]{2}$/.test(asText(territory?.c)) && Array.isArray(territory?.z))
      : [DEFAULT_PHONE_TERRITORY];
  }

  function territoryByCode(code) {
    const normalized = asText(code).trim().toUpperCase();
    return territories().find((territory) => territory.c === normalized) || null;
  }

  function territoryName(territory) {
    if (!territory) return '';
    const locale = document.documentElement.lang || 'es-MX';
    const cacheKey = `${locale}:${territory.c}`;
    if (territoryNameCache.has(cacheKey)) return territoryNameCache.get(cacheKey);
    let name = asText(territory.n) || territory.c;
    try {
      if (typeof Intl.DisplayNames === 'function') {
        const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
        const localized = displayNames.of(territory.c);
        if (localized && localized !== territory.c) name = localized;
      }
    } catch (_error) {
      // The static Shopify-derived name remains the controlled fallback.
    }
    territoryNameCache.set(cacheKey, name);
    return name;
  }

  function sortedTerritories(mode) {
    const list = territories().filter((territory) => mode !== 'phone' || /^[1-9]\d{0,3}$/.test(asText(territory.p)));
    try {
      const collator = new Intl.Collator(document.documentElement.lang || 'es-MX', { sensitivity: 'base' });
      return list.slice().sort((left, right) => collator.compare(territoryName(left), territoryName(right)));
    } catch (_error) {
      return list.slice().sort((left, right) => territoryName(left).localeCompare(territoryName(right)));
    }
  }

  function defaultPhoneCountry(root) {
    const shell = closestShell(root);
    const configured = asText(shell?.dataset?.pcDefaultPhoneCountry).trim().toUpperCase();
    return territoryByCode(configured)?.c || DEFAULT_PHONE_TERRITORY.c;
  }

  function phoneField(root) {
    if (!root) return null;
    if (root.matches?.('[data-pc-phone-field]')) return root;
    return root.closest?.('[data-pc-phone-field]') || root.querySelector?.('[data-pc-phone-field]') || null;
  }

  function phoneConfig(root, territoryCode = '') {
    const field = phoneField(root);
    const territorySelect = field?.querySelector?.('[data-pc-phone-territory]');
    const selectedCode = asText(territoryCode || (territorySelect
      ? territorySelect.value
      : (field?.dataset?.pcDefaultCountry || defaultPhoneCountry(root)))).trim().toUpperCase();
    const territory = territoryByCode(selectedCode);
    if (!territory) {
      return {
        territoryCode: selectedCode,
        prefix: '',
        prefixDigits: '',
        localLengths: [],
        minLocalLength: 0,
        maxLocalLength: 0,
        nationalPrefix: '',
        valid: false
      };
    }
    const prefixDigits = asText(territory?.p).replace(/\D/g, '');
    const configuredLengths = Array.isArray(territory?.l)
      ? territory.l.filter((length) => Number.isInteger(length) && length > 0 && length <= PHONE_MAX_TOTAL_DIGITS - prefixDigits.length)
      : [];
    const fallbackMinLength = Math.max(1, PHONE_MIN_TOTAL_DIGITS - prefixDigits.length);
    const fallbackMaxLength = Math.max(fallbackMinLength, PHONE_MAX_TOTAL_DIGITS - prefixDigits.length);
    const localLengths = configuredLengths.length
      ? Array.from(new Set(configuredLengths)).sort((left, right) => left - right)
      : Array.from({ length: fallbackMaxLength - fallbackMinLength + 1 }, (_value, index) => fallbackMinLength + index);
    const nationalPrefix = /^\d+$/.test(asText(territory?.t)) ? asText(territory.t) : '';
    return {
      territoryCode: territory?.c || selectedCode,
      prefix: prefixDigits ? `+${prefixDigits}` : '',
      prefixDigits,
      localLengths,
      minLocalLength: localLengths[0],
      maxLocalLength: localLengths[localLengths.length - 1],
      nationalPrefix,
      valid: /^[A-Z]{2}$/.test(asText(territory?.c)) && /^[1-9]\d{0,3}$/.test(prefixDigits) && localLengths.length > 0
    };
  }

  function territoryFromPhone(value, preferredCode = '') {
    const original = asText(value).trim();
    const digits = original.replace(/\D/g, '');
    if (!original.startsWith('+') || !digits) return territoryByCode(preferredCode);
    const matches = territories()
      .filter((territory) => territory.p && digits.startsWith(asText(territory.p)))
      .sort((left, right) => asText(right.p).length - asText(left.p).length);
    if (!matches.length) return territoryByCode(preferredCode);
    const longestPrefixLength = asText(matches[0].p).length;
    const longestMatches = matches.filter((territory) => asText(territory.p).length === longestPrefixLength);
    const preferred = longestMatches.find((territory) => territory.c === asText(preferredCode).trim().toUpperCase());
    if (preferred) return preferred;
    const sharedDefaults = { '1': 'US', '7': 'RU', '44': 'GB', '55': 'BR', '61': 'AU', '82': 'KR', '212': 'MA', '243': 'CD', '262': 'RE', '358': 'FI', '500': 'FK', '590': 'GP', '599': 'CW' };
    return longestMatches.find((territory) => territory.c === sharedDefaults[asText(territory.p)]) || longestMatches[0];
  }

  function restorePhoneField(target, value, preferredCode = '') {
    const field = phoneField(target);
    if (!field) return;
    const select = field.querySelector('[data-pc-phone-territory]');
    const input = field.querySelector('[data-pc-phone-local]');
    const territory = territoryFromPhone(value, preferredCode)
      || territoryByCode(preferredCode)
      || territoryByCode(defaultPhoneCountry(field));
    if (select && territory) {
      select.value = territory.c;
      syncTerritoryPicker(select);
    }
    if (input) input.value = localPhone(value, phoneConfig(field));
  }

  function renderProfile(root, customer) {
    setValue(root, '[data-pc-profile-field="firstName"]', customer?.firstName);
    setValue(root, '[data-pc-profile-field="lastName"]', customer?.lastName);
    setValue(root, '[data-pc-profile-field="email"]', customer?.email);
    restorePhoneField(root.querySelector('[data-pc-mutation-form="profile"]'), phoneValue(customer), defaultPhoneCountry(root));
  }

  function renderMarketing(root, customer) {
    const emailMarketing = root.querySelector('[data-pc-marketing-email]');
    const smsMarketing = root.querySelector('[data-pc-marketing-sms]');
    if (emailMarketing) emailMarketing.checked = customer?.marketing?.email?.subscribed === true;
    if (smsMarketing) smsMarketing.checked = customer?.marketing?.sms?.subscribed === true;
  }

  function renderCustomer(root, customer) {
    renderProfile(root, customer);
    renderMarketing(root, customer);
  }

  function fieldLine(address) {
    return [address?.city, address?.province, address?.zip].filter((value) => asText(value) !== '').map(asText).join(', ');
  }

  function addressName(address) {
    const full = [address?.firstName, address?.lastName].filter((value) => asText(value) !== '').map(asText).join(' ').trim();
    return full || asText(address?.name) || '—';
  }

  function addressCard(root, address) {
    const defaultAddressLabel = root.querySelector('[data-pc-address-default-label]')?.textContent?.trim() || '';
    const formattedLines = (Array.isArray(address?.formatted)
      ? address.formatted
      : (asText(address?.formatted) ? [address.formatted] : []))
      .filter((value) => asText(value) !== '');
    const card = document.createElement('article');
    const body = document.createElement('div');
    const actions = document.createElement('div');
    const heading = document.createElement('h3');
    const copy = document.createElement('address');
    card.className = 'pc-profile__address';
    card.dataset.pcAddressId = asText(address?.id);
    body.className = 'pc-profile__address-body';
    heading.className = 'pc-profile__address-name';
    heading.textContent = addressName(address);
    copy.className = 'pc-profile__address-copy';
    const headingText = addressName(address);
    const addressLines = formattedLines.length
      ? formattedLines
      : [address?.company, address?.address1, address?.address2, fieldLine(address), address?.country, phoneValue(address)];
    Array.from(new Set(addressLines.map((value) => asText(value).trim())))
      .filter((value) => value !== '' && value !== headingText)
      .forEach((line) => {
        const span = document.createElement('span');
        span.textContent = asText(line);
        copy.appendChild(span);
      });
    body.append(heading, copy);
    if (address?.isDefault === true && defaultAddressLabel) {
      const badge = document.createElement('span');
      badge.className = 'pc-badge pc-badge--success';
      badge.textContent = defaultAddressLabel;
      body.appendChild(badge);
    }
    actions.className = 'pc-profile__address-actions';
    const availableActions = address?.isDefault === true
      ? [['edit-address', 'pc-button--secondary'], ['delete-address', 'pc-button--danger']]
      : [['set-default-address', 'pc-button--secondary'], ['edit-address', 'pc-button--secondary'], ['delete-address', 'pc-button--danger']];
    availableActions.forEach(([action, className]) => {
      const labelName = action === 'set-default-address' ? 'set-default' : (action === 'edit-address' ? 'edit' : 'delete');
      const template = root.querySelector(`[data-pc-address-${labelName}-label]`);
      const button = document.createElement('button');
      button.className = `pc-button ${className}`;
      button.type = 'button';
      button.dataset.pcAction = action;
      button.dataset.pcAddressId = asText(address?.id);
      button.textContent = template?.textContent?.trim() || action;
      button.disabled = false;
      button.setAttribute('aria-disabled', 'false');
      actions.appendChild(button);
    });
    card.append(body, actions);
    return card;
  }

  function renderAddresses(root, addresses) {
    const list = root.querySelector('[data-pc-address-list]');
    const empty = root.querySelector('[data-pc-empty-addresses]');
    const count = root.querySelector('[data-pc-address-count]');
    if (!list) return;
    list.replaceChildren();
    addresses.forEach((address) => list.appendChild(addressCard(root, address)));
    if (count) {
      const template = count.dataset.pcAddressCountTemplate || '__COUNT__ dirección guardada';
      count.textContent = template.replace('__COUNT__', String(addresses.length));
      count.dataset.pcAddressCount = String(addresses.length);
    }
    if (empty) empty.hidden = addresses.length > 0;
  }

  function configureMutationControls(root, { profileWritable = true, addressWritable = true } = {}) {
    const setEnabled = (control, enabled) => {
      control.disabled = !enabled;
      control.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    };
    interactionScopes(root).forEach((scope) => {
      scope.querySelectorAll('[data-pc-profile-field], [data-pc-marketing-email], [data-pc-marketing-sms]').forEach((control) => {
        if (control.matches('[data-pc-profile-field="email"]')) {
          control.readOnly = true;
          setEnabled(control, false);
          return;
        }
        setEnabled(control, profileWritable);
      });
      scope.querySelectorAll('[data-pc-address-field], [data-pc-action="add-address"], [data-pc-action="set-default-address"], [data-pc-action="edit-address"], [data-pc-action="delete-address"]').forEach((control) => setEnabled(control, addressWritable));
      scope.querySelectorAll('[data-pc-mutation-form="profile"], [data-pc-mutation-form="marketing"]').forEach((form) => form.setAttribute('aria-disabled', profileWritable ? 'false' : 'true'));
      scope.querySelectorAll('[data-pc-mutation-form="address"]').forEach((form) => form.setAttribute('aria-disabled', addressWritable ? 'false' : 'true'));
      scope.querySelectorAll('[data-pc-mutation-form="profile"] button[type="submit"]').forEach((control) => setEnabled(control, profileWritable));
      scope.querySelectorAll('[data-pc-mutation-form="address"] button[type="submit"], [data-pc-action="confirm-delete-address"]').forEach((control) => setEnabled(control, addressWritable));
      scope.querySelectorAll('[data-pc-profile-write-unavailable], [data-pc-marketing-write-unavailable]').forEach((notice) => {
        notice.hidden = profileWritable;
      });
      scope.querySelectorAll('[data-pc-address-write-unavailable]').forEach((notice) => {
        notice.hidden = addressWritable;
      });
      scope.querySelectorAll('[data-pc-territory-select]').forEach(syncTerritoryPicker);
    });
  }

  function dialog(root, name = 'address') {
    const local = root?.querySelector?.(`[data-pc-dialog="${name}"]`);
    if (local) return local;
    const shell = closestShell(root);
    return shell?.querySelector?.(`[data-pc-dialog-portal] [data-pc-dialog-owner="profile"][data-pc-dialog="${name}"], [data-pc-dialog-portal] [data-pc-dialog="${name}"]`) || null;
  }

  function interactionScopes(root) {
    const scopes = [root];
    ['address', 'delete-address'].forEach((name) => {
      const target = dialog(root, name);
      if (target && !root.contains(target) && !scopes.includes(target)) scopes.push(target);
    });
    return scopes;
  }

  function ensureSelectOption(select, value, label, dataName) {
    const normalizedValue = asText(value).trim();
    if (!select || !normalizedValue) return null;
    let option = Array.from(select.options).find((item) => item.value === normalizedValue);
    if (!option) {
      option = document.createElement('option');
      option.value = normalizedValue;
      option.textContent = asText(label).trim() || normalizedValue;
      select.appendChild(option);
    }
    if (dataName && asText(label).trim()) option.dataset[dataName] = asText(label).trim();
    return option;
  }

  function populateTerritorySelect(select) {
    if (!select) return;
    const mode = select.dataset.pcPickerMode === 'phone' ? 'phone' : 'country';
    const previousValue = asText(select.value).trim().toUpperCase();
    const placeholder = mode === 'country'
      ? Array.from(select.options).find((option) => option.value === '')?.textContent || ''
      : '';
    const fragment = document.createDocumentFragment();
    if (placeholder) {
      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = placeholder;
      fragment.appendChild(emptyOption);
    }
    sortedTerritories(mode).forEach((territory) => {
      const option = document.createElement('option');
      const name = territoryName(territory);
      option.value = territory.c;
      option.textContent = mode === 'phone' ? `${name} (+${territory.p})` : name;
      option.dataset.pcCountryName = name;
      if (territory.p) option.dataset.pcPhonePrefix = `+${territory.p}`;
      fragment.appendChild(option);
    });
    select.replaceChildren(fragment);
    const fallback = asText(select.dataset.pcDefaultValue
      || select.closest('[data-pc-phone-field]')?.dataset?.pcDefaultCountry
      || defaultPhoneCountry(select)).trim().toUpperCase();
    const desired = Array.from(select.options).some((option) => option.value === previousValue) ? previousValue : fallback;
    if (Array.from(select.options).some((option) => option.value === desired)) select.value = desired;
    else if (mode === 'phone' && select.options.length) select.selectedIndex = 0;
  }

  function prepareTerritorySelects(root) {
    interactionScopes(root).forEach((scope) => {
      scope.querySelectorAll('[data-pc-territory-select]').forEach(populateTerritorySelect);
    });
  }

  function territoryPickerFor(select) {
    const next = select?.nextElementSibling;
    return next?.matches?.('[data-pc-territory-picker]') ? next : null;
  }

  function syncTerritoryPicker(select) {
    const picker = territoryPickerFor(select);
    if (!select || !picker) return;
    const territory = territoryByCode(select.value);
    const mode = select.dataset.pcPickerMode === 'phone' ? 'phone' : 'country';
    const trigger = picker.querySelector('[data-pc-territory-trigger]');
    const value = picker.querySelector('[data-pc-territory-value]');
    const name = territoryName(territory);
    const visible = territory
      ? (mode === 'phone' ? `+${territory.p}` : name)
      : select.selectedOptions?.[0]?.textContent || select.dataset.pcPickerLabel || '';
    if (value) value.textContent = visible;
    if (trigger) {
      trigger.disabled = select.disabled;
      trigger.setAttribute('aria-disabled', String(select.disabled));
      trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') === 'true' ? 'true' : 'false');
      trigger.setAttribute('aria-label', [select.dataset.pcPickerLabel, name, mode === 'phone' && territory?.p ? `+${territory.p}` : ''].filter(Boolean).join(': '));
      const describedBy = select.getAttribute('aria-describedby');
      if (describedBy) trigger.setAttribute('aria-describedby', describedBy);
      else trigger.removeAttribute('aria-describedby');
    }
    picker.querySelectorAll('[data-pc-territory-option]').forEach((option) => {
      const selected = option.dataset.pcTerritoryOption === select.value;
      option.setAttribute('aria-selected', String(selected));
      option.dataset.pcSelected = String(selected);
    });
  }

  function closeTerritoryPicker(picker, restoreFocus = false) {
    if (!picker) return;
    const panel = picker.querySelector('[data-pc-territory-panel]');
    const trigger = picker.querySelector('[data-pc-territory-trigger]');
    if (panel) panel.hidden = true;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    }
  }

  function closeOtherTerritoryPickers(activePicker) {
    document.querySelectorAll('[data-pc-territory-picker]').forEach((picker) => {
      if (picker !== activePicker) closeTerritoryPicker(picker);
    });
  }

  function renderTerritoryOptions(select, picker, query = '') {
    const mode = select.dataset.pcPickerMode === 'phone' ? 'phone' : 'country';
    const list = picker.querySelector('[data-pc-territory-list]');
    const empty = picker.querySelector('[data-pc-territory-empty]');
    if (!list || !empty) return;
    const needle = foldedText(query);
    const matches = sortedTerritories(mode).filter((territory) => {
      const searchValue = [territoryName(territory), territory.n, territory.c, territory.p ? `+${territory.p}` : ''].join(' ');
      return !needle || foldedText(searchValue).includes(needle);
    });
    const fragment = document.createDocumentFragment();
    matches.forEach((territory) => {
      const option = document.createElement('button');
      const name = document.createElement('span');
      const dial = document.createElement('span');
      option.type = 'button';
      option.className = 'pc-territory-picker__option';
      option.dataset.pcTerritoryOption = territory.c;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(territory.c === select.value));
      option.tabIndex = -1;
      name.className = 'pc-territory-picker__name';
      name.textContent = territoryName(territory);
      dial.className = 'pc-territory-picker__dial';
      dial.textContent = territory.p ? `+${territory.p}` : territory.c;
      option.append(name, dial);
      fragment.appendChild(option);
    });
    list.replaceChildren(fragment);
    empty.hidden = matches.length > 0;
    syncTerritoryPicker(select);
  }

  function enhanceTerritorySelect(select, state) {
    if (!select) return;
    populateTerritorySelect(select);
    let picker = territoryPickerFor(select);
    if (!picker) {
      territoryPickerId += 1;
      const id = `pc-territory-picker-${territoryPickerId}`;
      const listId = `${id}-list`;
      picker = document.createElement('div');
      picker.className = 'pc-territory-picker';
      picker.dataset.pcTerritoryPicker = 'true';
      picker.innerHTML = `<button class="pc-territory-picker__trigger" type="button" aria-haspopup="listbox" aria-expanded="false" aria-controls="${listId}" data-pc-territory-trigger><span class="pc-territory-picker__value" data-pc-territory-value></span><span class="pc-territory-picker__chevron" aria-hidden="true">⌄</span></button><div class="pc-territory-picker__panel" id="${id}-panel" data-pc-territory-panel hidden><label class="pc-territory-picker__search-label" for="${id}-search"></label><input class="pc-territory-picker__search" id="${id}-search" type="search" autocomplete="off" data-pc-territory-search><div class="pc-territory-picker__list" id="${listId}" role="listbox" data-pc-territory-list></div><p class="pc-territory-picker__empty" role="status" data-pc-territory-empty hidden></p></div>`;
      picker.querySelector('.pc-territory-picker__search-label').textContent = asText(select.dataset.pcPickerLabel);
      select.insertAdjacentElement('afterend', picker);
    }
    const shell = closestShell(select);
    const panel = picker.querySelector('[data-pc-territory-panel]');
    const trigger = picker.querySelector('[data-pc-territory-trigger]');
    const search = picker.querySelector('[data-pc-territory-search]');
    const list = picker.querySelector('[data-pc-territory-list]');
    const empty = picker.querySelector('[data-pc-territory-empty]');
    const searchPlaceholder = select.dataset.pcPickerMode === 'phone'
      ? shell?.dataset?.pcPhoneCountrySearchPlaceholder
      : shell?.dataset?.pcCountrySearchPlaceholder;
    search.placeholder = asText(searchPlaceholder);
    search.setAttribute('aria-label', asText(searchPlaceholder || select.dataset.pcPickerLabel));
    list.setAttribute('aria-label', asText(select.dataset.pcPickerLabel));
    empty.textContent = asText(shell?.dataset?.pcTerritoryEmpty);
    renderTerritoryOptions(select, picker);
    select.dataset.pcTerritoryReady = 'true';
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    const open = () => {
      if (select.disabled) return;
      closeOtherTerritoryPickers(picker);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      search.value = '';
      renderTerritoryOptions(select, picker);
      search.focus();
    };
    const selectOption = (option) => {
      if (!option?.dataset?.pcTerritoryOption) return;
      select.value = option.dataset.pcTerritoryOption;
      syncTerritoryPicker(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closeTerritoryPicker(picker, true);
    };
    const focusRelativeOption = (direction) => {
      const options = Array.from(list.querySelectorAll('[data-pc-territory-option]'));
      if (!options.length) return;
      const current = options.indexOf(document.activeElement);
      const next = current < 0
        ? options.findIndex((option) => option.getAttribute('aria-selected') === 'true')
        : (current + direction + options.length) % options.length;
      (options[next < 0 ? 0 : next] || options[0]).focus();
    };
    addListener(state, trigger, 'click', () => panel.hidden ? open() : closeTerritoryPicker(picker));
    addListener(state, trigger, 'keydown', (event) => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        open();
        focusRelativeOption(event.key === 'ArrowDown' ? 1 : -1);
      }
    });
    addListener(state, search, 'input', () => renderTerritoryOptions(select, picker, search.value));
    addListener(state, search, 'keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeTerritoryPicker(picker, true);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusRelativeOption(1);
      }
    });
    addListener(state, list, 'click', (event) => selectOption(event.target.closest('[data-pc-territory-option]')));
    addListener(state, list, 'keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeTerritoryPicker(picker, true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        focusRelativeOption(event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const options = Array.from(list.querySelectorAll('[data-pc-territory-option]'));
        (event.key === 'Home' ? options[0] : options[options.length - 1])?.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        selectOption(event.target.closest('[data-pc-territory-option]'));
      }
    });
    addListener(state, select, 'change', () => syncTerritoryPicker(select));
    addListener(state, document, 'pointerdown', (event) => {
      if (!picker.contains(event.target)) closeTerritoryPicker(picker);
    });
    syncTerritoryPicker(select);
  }

  function enhanceTerritoryControls(root, state) {
    interactionScopes(root).forEach((scope) => {
      scope.querySelectorAll('[data-pc-territory-select]').forEach((select) => enhanceTerritorySelect(select, state));
    });
  }

  function sourceCountryOption(target, countryName) {
    const expected = foldedText(countryName);
    if (!expected) return null;
    const source = target.querySelector('[data-pc-address-territory-source]');
    return Array.from(source?.options || []).find((option) => {
      return foldedText(option.value) === expected || foldedText(option.textContent) === expected;
    }) || null;
  }

  function normalizeProvinceCode(countryCode, sourceValue, provinceName) {
    const source = asText(sourceValue).trim();
    const territory = territoryByCode(countryCode);
    if (!territory?.z?.length) return source;
    const exact = territory.z.find(([code]) => asText(code).toUpperCase() === source.toUpperCase());
    if (exact) return exact[0];
    const expectedName = foldedText(provinceName || source);
    const byName = territory.z.find(([, name]) => foldedText(name) === expectedName);
    return byName?.[0] || source;
  }

  function provincePairs(target, countryName, countryCode) {
    const territory = territoryByCode(countryCode);
    const sourceOption = sourceCountryOption(target, countryName) || sourceCountryOption(target, territory?.n);
    let localizedPairs = [];
    try {
      localizedPairs = JSON.parse(sourceOption?.dataset?.provinces || '[]');
      if (!Array.isArray(localizedPairs)) localizedPairs = [];
    } catch (_error) {
      localizedPairs = [];
    }
    if (localizedPairs.length) {
      const seen = new Set();
      return localizedPairs.reduce((result, pair) => {
        if (!Array.isArray(pair) || !asText(pair[0]).trim() || !asText(pair[1]).trim()) return result;
        const name = asText(pair[1]).trim();
        const code = normalizeProvinceCode(countryCode, pair[0], name);
        const key = asText(code).toUpperCase() || foldedText(name);
        if (!key || seen.has(key)) return result;
        seen.add(key);
        result.push([code, name]);
        return result;
      }, []);
    }
    return Array.isArray(territory?.z) ? territory.z.slice() : [];
  }

  function syncAddressNames(target) {
    const countrySelect = target.querySelector('[data-pc-address-country]');
    const provinceSelect = target.querySelector('[data-pc-address-province]');
    const countryName = target.querySelector('[data-pc-address-field="country"]');
    const provinceName = target.querySelector('[data-pc-address-field="province"]');
    const countryOption = countrySelect?.selectedOptions?.[0];
    const provinceOption = provinceSelect?.selectedOptions?.[0];
    if (countryName) countryName.value = countryOption?.dataset?.pcCountryName || '';
    if (provinceName) provinceName.value = provinceOption?.dataset?.pcProvinceName || '';
  }

  function configurePostalCode(target, countryCode) {
    const postalCode = target.querySelector('[data-pc-address-field="zip"]');
    if (!postalCode) return;
    if (asText(countryCode).toUpperCase() === 'MX') {
      postalCode.pattern = '[0-9]{5}';
      postalCode.inputMode = 'numeric';
      postalCode.maxLength = 5;
      return;
    }
    postalCode.removeAttribute('pattern');
    postalCode.removeAttribute('inputmode');
    postalCode.removeAttribute('maxlength');
  }

  function syncAddressTerritories(target, address = {}) {
    const countrySelect = target.querySelector('[data-pc-address-country]');
    const provinceSelect = target.querySelector('[data-pc-address-province]');
    const provinceField = target.querySelector('[data-pc-address-province-field]');
    if (!countrySelect || !provinceSelect || !provinceField) return;

    const requestedCountryCode = asText(addressValue(address, 'countryCode')).trim().toUpperCase();
    const requestedCountryName = asText(address.country).trim();
    if (requestedCountryCode) ensureSelectOption(countrySelect, requestedCountryCode, requestedCountryName, 'pcCountryName');
    if (requestedCountryCode && Array.from(countrySelect.options).some((option) => option.value === requestedCountryCode)) {
      countrySelect.value = requestedCountryCode;
    } else if (requestedCountryName) {
      const match = Array.from(countrySelect.options).find((option) => foldedText(option.dataset.pcCountryName || option.textContent) === foldedText(requestedCountryName));
      if (match) countrySelect.value = match.value;
    } else {
      countrySelect.value = countrySelect.dataset.pcDefaultValue || '';
    }

    const countryOption = countrySelect.selectedOptions?.[0];
    const countryName = countryOption?.dataset?.pcCountryName || requestedCountryName;
    const pairs = provincePairs(target, countryName, countrySelect.value);
    const placeholder = provinceSelect.dataset.pcPlaceholder || '';
    const requestedProvinceCode = normalizeProvinceCode(countrySelect.value, addressValue(address, 'provinceCode'), address.province);
    const requestedProvinceName = asText(address.province).trim();
    provinceSelect.replaceChildren();
    if (pairs.length) {
      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = placeholder;
      provinceSelect.appendChild(emptyOption);
      pairs.forEach(([code, name]) => {
        const option = document.createElement('option');
        option.value = asText(code).trim();
        option.textContent = asText(name).trim();
        option.dataset.pcProvinceName = asText(name).trim();
        provinceSelect.appendChild(option);
      });
    }
    const options = Array.from(provinceSelect.options);
    const codeMatch = requestedProvinceCode
      ? options.find((option) => option.value.toUpperCase() === requestedProvinceCode.toUpperCase())
      : null;
    const nameMatch = requestedProvinceName
      ? options.find((option) => foldedText(option.dataset.pcProvinceName || option.textContent) === foldedText(requestedProvinceName))
      : null;
    const selectedOption = codeMatch || nameMatch
      || (requestedProvinceCode ? ensureSelectOption(provinceSelect, requestedProvinceCode, requestedProvinceName, 'pcProvinceName') : null);
    if (selectedOption) provinceSelect.value = selectedOption.value;
    const hasProvinceChoices = provinceSelect.options.length > 0;
    provinceField.hidden = !hasProvinceChoices;
    provinceSelect.required = hasProvinceChoices;
    provinceSelect.disabled = !hasProvinceChoices;
    provinceSelect.setAttribute('aria-disabled', hasProvinceChoices ? 'false' : 'true');
    configurePostalCode(target, countrySelect.value);
    syncAddressNames(target);
    syncTerritoryPicker(countrySelect);
    const addressPhoneField = target.querySelector('[data-pc-phone-field]');
    const savedPhone = addressValue(address, 'phone');
    if (savedPhone) {
      restorePhoneField(addressPhoneField, savedPhone, countrySelect.value);
    } else {
      const phoneTerritory = addressPhoneField?.querySelector('[data-pc-phone-territory]');
      const phoneControl = addressPhoneField?.querySelector('[data-pc-phone-local]');
      const linkedTerritory = territoryByCode(countrySelect.value);
      if (phoneTerritory) {
        phoneTerritory.value = linkedTerritory?.p ? countrySelect.value : '';
        syncTerritoryPicker(phoneTerritory);
      }
      if (!linkedTerritory?.p && phoneControl) {
        phoneControl.value = '';
        phoneControl.setCustomValidity('');
      }
    }
    validatePhoneControl(target.querySelector('[data-pc-phone-local]'), target);
  }

  function normalizePhone(value, config) {
    const original = asText(value).trim();
    if (!original) return '';
    const digits = original.replace(/\D/g, '');
    if (original.startsWith('+') && digits.length >= PHONE_MIN_TOTAL_DIGITS && digits.length <= PHONE_MAX_TOTAL_DIGITS) return `+${digits}`;
    const resolved = config || phoneConfig(null);
    if (resolved.valid === false) return original;
    const local = localPhone(original, resolved);
    return resolved.localLengths.includes(local.length)
      ? `${resolved.prefix}${local}`
      : original;
  }

  function localPhone(value, config) {
    const resolved = config || phoneConfig(null);
    const original = asText(value).trim();
    const digits = asText(value).replace(/\D/g, '');
    const hasSelectedPrefix = resolved.prefixDigits && digits.startsWith(resolved.prefixDigits);
    let local = hasSelectedPrefix && (original.startsWith('+') || digits.length > resolved.maxLocalLength)
      ? digits.slice(resolved.prefixDigits.length)
      : digits;
    if (
      !original.startsWith('+')
      && resolved.nationalPrefix
      && local.startsWith(resolved.nationalPrefix)
      && !resolved.localLengths.includes(local.length)
      && resolved.localLengths.includes(local.length - resolved.nationalPrefix.length)
    ) {
      local = local.slice(resolved.nationalPrefix.length);
    }
    return local;
  }

  function validatePhoneControl(control, root) {
    if (!control) return true;
    const config = phoneConfig(control);
    const local = localPhone(control.value, config);
    const shell = closestShell(root || control);
    let message = '';
    control.removeAttribute('minlength');
    control.removeAttribute('maxlength');
    if (config.valid && config.minLocalLength > 0 && config.maxLocalLength >= config.minLocalLength) {
      control.setAttribute('maxlength', String(config.maxLocalLength));
      control.setAttribute('minlength', String(config.minLocalLength));
    }
    if (local && (!config.valid || !config.localLengths.includes(local.length))) {
      message = shell?.dataset?.pcPhoneInvalid || 'Ingresa un número de teléfono válido para el prefijo seleccionado.';
    }
    control.setCustomValidity(message);
    return message === '';
  }

  function normalizeLocalPhoneControl(control, root) {
    if (!control) return;
    const config = phoneConfig(control);
    control.value = localPhone(control.value, config);
    validatePhoneControl(control, root || control);
  }

  function fillAddressDialog(root, address) {
    const target = dialog(root) || root;
    target.querySelectorAll('[data-pc-address-field]').forEach((control) => {
      const key = control.dataset.pcAddressField;
      if (key === 'phone' || key === 'phoneCountryCode') return;
      if (control.type === 'checkbox') control.checked = address?.[key] === true;
      else control.value = asText(addressValue(address, key));
    });
    syncAddressTerritories(target, address || {});
  }

  function focusableIn(element) {
    return Array.from(element.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((node) => !node.hidden && node.getClientRects().length > 0);
  }

  function closeDialog(root, state, restoreFocus, name) {
    const target = name ? dialog(root, name) : ['address', 'delete-address'].map((key) => dialog(root, key)).find((item) => item && !item.hidden);
    if (!target || target.hidden) return;
    target.querySelectorAll('[data-pc-territory-picker]').forEach((picker) => closeTerritoryPicker(picker));
    target.hidden = true;
    if (restoreFocus) {
      const focusTarget = state.lastFocus?.isConnected ? state.lastFocus : root.querySelector('[data-pc-action="add-address"]') || root;
      focusTarget?.focus?.();
    }
    state.lastFocus = null;
    if (target.dataset.pcDialog === 'delete-address') state.pendingDeleteAddress = null;
  }

  function openAddressDialog(root, state, trigger) {
    const addressId = asText(trigger?.dataset?.pcAddressId);
    const address = addressId ? state.addresses.find((item) => sameAddressId(item?.id, addressId)) : null;
    if (addressId && !address) return false;
    const target = dialog(root);
    if (!target) return false;
    state.lastFocus = trigger || document.activeElement;
    fillAddressDialog(root, address || {});
    const title = target.querySelector('[data-pc-address-dialog-title]');
    const titleLabel = root.querySelector(address ? '[data-pc-address-dialog-edit-label]' : '[data-pc-address-dialog-add-label]');
    if (title && titleLabel?.textContent?.trim()) title.textContent = titleLabel.textContent.trim();
    target.hidden = false;
    const formBody = target.querySelector('.pc-profile__address-form-body');
    if (formBody) formBody.scrollTop = 0;
    const initialFocus = target.querySelector('[data-pc-address-field]:not([type="hidden"]):not([disabled])') || focusableIn(target)[0] || target;
    initialFocus.focus();
    return true;
  }

  function openDeleteDialog(root, state, trigger) {
    const addressId = asText(trigger?.dataset?.pcAddressId);
    const address = state.addresses.find((item) => sameAddressId(item?.id, addressId));
    const target = dialog(root, 'delete-address');
    if (!address || !target) return false;
    state.lastFocus = trigger || document.activeElement;
    state.pendingDeleteAddress = address;
    target.hidden = false;
    (target.querySelector('[data-pc-action="confirm-delete-address"]') || focusableIn(target)[0] || target).focus();
    return true;
  }

  function installLocalGuards(root, state) {
    clearListeners(state);
    enhanceTerritoryControls(root, state);
    ['address', 'delete-address'].forEach((name) => {
      const keyboardScope = dialog(root, name);
      if (!keyboardScope) return;
      addListener(state, keyboardScope, 'keydown', (event) => {
        if (keyboardScope.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDialog(root, state, true, name);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = focusableIn(keyboardScope);
        if (!focusable.length) {
          event.preventDefault();
          keyboardScope.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !keyboardScope.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !keyboardScope.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      });
    });
    const addressDialog = dialog(root, 'address');
    const countrySelect = addressDialog?.querySelector?.('[data-pc-address-country]');
    const provinceSelect = addressDialog?.querySelector?.('[data-pc-address-province]');
    if (countrySelect) {
      addListener(state, countrySelect, 'change', () => {
        syncAddressTerritories(addressDialog, { countryCode: countrySelect.value });
      });
    }
    if (provinceSelect) addListener(state, provinceSelect, 'change', () => syncAddressNames(addressDialog));
    interactionScopes(root).forEach((scope) => scope.querySelectorAll('[data-pc-phone-territory]').forEach((select) => {
      addListener(state, select, 'change', () => {
        const field = select.closest('[data-pc-phone-field]');
        normalizeLocalPhoneControl(field?.querySelector('[data-pc-phone-local]'), field || root);
      });
    }));
    interactionScopes(root).forEach((scope) => scope.querySelectorAll('[data-pc-field]').forEach((control) => {
      if (!control.name || control.type === 'hidden') return;
      const refresh = () => {
        if (control.matches('[data-pc-phone-local]')) {
          normalizeLocalPhoneControl(control, root);
        }
        if (control.form && control.checkValidity()) clearFieldError(control.form, control);
      };
      addListener(state, control, control.matches('select, input[type="checkbox"]') ? 'change' : 'input', refresh);
    }));
    interactionScopes(root).forEach((scope) => scope.querySelectorAll('[data-pc-phone-local]').forEach((control) => {
      normalizeLocalPhoneControl(control, root);
    }));
  }

  function formMessage(root, name, message, kind = '') {
    const target = interactionScopes(root).map((scope) => scope.querySelector(`[data-pc-form-status="${name}"]`)).find(Boolean);
    if (!target) return;
    target.textContent = asText(message);
    delete target.dataset.pcMessageType;
    if (kind) target.dataset.pcStatusType = kind;
    else delete target.dataset.pcStatusType;
  }

  function fieldError(form, control) {
    if (!form || !control?.name) return null;
    return Array.from(form.querySelectorAll('[data-pc-error-for]'))
      .find((target) => target.dataset.pcErrorFor === control.name) || null;
  }

  function fieldValidationMessage(root, control) {
    const shell = closestShell(root || control);
    if (control?.validity?.customError) return asText(control.validationMessage);
    if (control?.validity?.valueMissing) return shell?.dataset?.pcRequiredMessage || 'Completa este campo.';
    if (control?.name === 'phone') return shell?.dataset?.pcPhoneInvalid || 'Ingresa un número de teléfono válido.';
    if (control?.name === 'postalCode') return shell?.dataset?.pcPostalInvalid || 'Ingresa un código postal válido.';
    return shell?.dataset?.pcFieldInvalid || 'Revisa el formato de este campo.';
  }

  function clearFieldError(form, control) {
    const target = fieldError(form, control);
    if (target) {
      target.textContent = '';
      target.hidden = true;
    }
    control?.removeAttribute?.('aria-invalid');
    if (control?.matches?.('[data-pc-territory-select]')) syncTerritoryPicker(control);
  }

  function showFieldError(root, form, control) {
    const target = fieldError(form, control);
    if (target) {
      target.textContent = fieldValidationMessage(root, control);
      target.hidden = false;
    }
    control?.setAttribute?.('aria-invalid', 'true');
    if (control?.matches?.('[data-pc-territory-select]')) syncTerritoryPicker(control);
  }

  function setBusy(form, busy) {
    const controls = form
      ? Array.from(form.querySelectorAll('button[type="submit"], [data-pc-action="confirm-delete-address"], input, select, textarea'))
      : [];
    if (form?.matches?.('button, input, select, textarea')) controls.unshift(form);
    controls.forEach((control) => {
      if (control.matches('[data-pc-profile-field="email"]')) return;
      control.disabled = busy;
      control.setAttribute('aria-disabled', String(busy));
    });
    form?.querySelectorAll('[data-pc-territory-select]').forEach(syncTerritoryPicker);
    form?.querySelectorAll('[data-pc-label="idle"]').forEach((label) => { label.hidden = busy; });
    form?.querySelectorAll('[data-pc-label="busy"]').forEach((label) => { label.hidden = !busy; });
    if (form) form.setAttribute('aria-busy', String(busy));
  }

  function isAuthenticationFailure(error) {
    return error?.code === 'UNAUTHORIZED' || error?.code === 'AUTH_REQUIRED' || error?.status === 401;
  }

  async function mutate(root, state, form, name, operation, surface = name) {
    const previous = state.mutationOperations.get(surface);
    previous?.controller.abort();
    if (previous?.target && previous.target !== form) setBusy(previous.target, false);
    const controller = new AbortController();
    const entry = { controller, target: form };
    state.mutationOperations.set(surface, entry);
    formMessage(root, name, '');
    setBusy(form, true);
    try {
      return await operation(controller.signal);
    } catch (error) {
      if (error?.name === ABORTED) return MUTATION_ABORTED;
      if (isAuthenticationFailure(error)) throw error;
      const shell = closestShell(root);
      const message = shell?.dataset?.pcMutationError || 'No se pudieron guardar los cambios.';
      formMessage(root, name, message, 'error');
      shell?.announce?.(message, 'error');
      return null;
    } finally {
      const active = state.mutationOperations.get(surface);
      if (active === entry) {
        state.mutationOperations.delete(surface);
        setBusy(form, false);
      } else if (active?.target !== form) {
        setBusy(form, false);
      }
    }
  }

  function validateForm(root, form, name) {
    const controls = form
      ? Array.from(form.elements).filter((control) => control.willValidate && !control.disabled)
      : [];
    controls.forEach((control) => clearFieldError(form, control));
    const invalidControls = controls.filter((control) => !control.checkValidity());
    if (!invalidControls.length) return true;
    invalidControls.forEach((control) => showFieldError(root, form, control));
    formMessage(root, name, '');
    const firstInvalid = invalidControls[0];
    const pickerTrigger = firstInvalid.matches?.('[data-pc-territory-select]')
      ? territoryPickerFor(firstInvalid)?.querySelector('[data-pc-territory-trigger]')
      : null;
    (pickerTrigger || firstInvalid).focus();
    return false;
  }

  function profileInput(form, customer) {
    const value = (name) => asText(form?.elements?.namedItem?.(name)?.value).trim();
    const config = phoneConfig(form);
    return ['firstName', 'lastName', 'phone'].reduce((input, name) => {
      const nextValue = name === 'phone' ? normalizePhone(value(name), config) : value(name);
      const currentValue = name === 'phone' ? normalizePhone(phoneValue(customer), config) : asText(customer?.[name]).trim();
      if (nextValue !== currentValue) input[name] = nextValue;
      return input;
    }, {});
  }

  function profileReadbackMatches(input, customer, root) {
    const config = phoneConfig(root);
    return Object.entries(input).every(([name, value]) => {
      if (name === 'phone') {
        return normalizePhone(value, config) === normalizePhone(phoneValue(customer), config);
      }
      return asText(value).trim() === asText(customer?.[name]).trim();
    });
  }

  function marketingInput(control, customer) {
    if (control?.matches?.('[data-pc-marketing-email]')) {
      const subscribed = control.checked === true;
      return subscribed === (customer?.marketing?.email?.subscribed === true) ? null : { acceptsEmailMarketing: subscribed };
    }
    if (control?.matches?.('[data-pc-marketing-sms]')) {
      const subscribed = control.checked === true;
      return subscribed === (customer?.marketing?.sms?.subscribed === true) ? null : { acceptsSmsMarketing: subscribed };
    }
    return null;
  }

  function addressInput(form) {
    const value = (name) => asText(form?.elements?.namedItem?.(name)?.value).trim();
    const countryCode = value('countryCode');
    const config = phoneConfig(form);
    return {
      firstName: value('firstName'), lastName: value('lastName'), company: value('company'),
      address1: value('address1'), address2: value('address2'), city: value('city'),
      province: value('province'), provinceCode: value('provinceCode'), country: value('country'),
      countryCode, zip: value('postalCode'), phone: normalizePhone(value('phone'), config),
      setAsDefault: form?.querySelector('[data-pc-address-field="isDefault"]')?.checked === true
    };
  }

  function defaultAddressInput(address, root) {
    const value = (name) => asText(addressValue(address, name)).trim();
    const config = phoneConfig(root);
    const countryCode = value('countryCode');
    return {
      firstName: value('firstName'), lastName: value('lastName'), company: value('company'),
      address1: value('address1'), address2: value('address2'), city: value('city'),
      province: value('province'), provinceCode: normalizeProvinceCode(countryCode, value('provinceCode'), value('province')), country: value('country'),
      countryCode, zip: value('zip'), phone: normalizePhone(value('phone'), config),
      setAsDefault: true
    };
  }

  function profileMutationAvailable(client, response, customer) {
    return response?.profile?.success === true
      && customer != null
      && typeof client?.getProfile === 'function'
      && typeof client?.updateProfile === 'function';
  }

  function addressMutationAvailable(client, response, addresses) {
    return response?.addresses?.success === true
      && Array.isArray(addresses)
      && typeof client?.listAddresses === 'function'
      && ['createAddress', 'updateAddress', 'deleteAddress'].every((name) => typeof client?.[name] === 'function');
  }

  async function readCustomer(client, signal) {
    const customer = dataOf(await client.getProfile({ signal }));
    if (!customer || typeof customer !== 'object' || Array.isArray(customer)) throw new Error('Profile readback failed.');
    return customer;
  }

  async function readAddresses(client, signal) {
    const addresses = dataOf(await client.listAddresses({ signal }));
    if (!Array.isArray(addresses)) throw new Error('Address readback failed.');
    return addresses;
  }

  async function loadProfile({ client, signal }) {
    const results = await Promise.allSettled([
      client.getProfile({ signal }),
      client.listAddresses({ signal })
    ]);
    if (signal?.aborted || results.some((result) => result.status === 'rejected' && result.reason?.name === ABORTED)) throw abortError();
    const [profile, addresses] = results;
    if (profile.status === 'rejected' && addresses.status === 'rejected') throw profile.reason || addresses.reason;
    return {
      profile: profile.status === 'fulfilled' ? profile.value : null,
      addresses: addresses.status === 'fulfilled' ? addresses.value : null,
      partial: profile.status === 'rejected' || addresses.status === 'rejected'
    };
  }

  function savedMessage(root) {
    return closestShell(root)?.dataset?.pcSavedMessage || 'Cambios guardados.';
  }

  async function saveProfile(root, state, client, shell, form) {
    if (typeof client?.updateProfile !== 'function' || typeof client?.getProfile !== 'function') return { unavailable: true };
    normalizeLocalPhoneControl(form?.elements?.namedItem?.('phone'), root);
    if (!validateForm(root, form, 'profile')) return true;
    const input = profileInput(form, state.customer);
    if (Object.keys(input).length === 0) return true;
    const result = await mutate(root, state, form, 'profile', async (signal) => {
      await client.updateProfile({ profile: input, signal });
      const customer = await readCustomer(client, signal);
      return { customer, persisted: profileReadbackMatches(input, customer, root) };
    });
    if (result === MUTATION_ABORTED) return true;
    if (!result) return true;
    const { customer, persisted } = result;
    state.customer = Object.assign({}, state.customer || {}, customer, { marketing: state.customer?.marketing ?? customer.marketing });
    state.profileWritable = true;
    renderProfile(root, state.customer);
    if (!persisted) {
      const message = shell?.dataset?.pcMutationError || 'No se pudieron guardar los cambios.';
      formMessage(root, 'profile', message, 'error');
      shell?.announce?.(message, 'error');
      return true;
    }
    formMessage(root, 'profile', savedMessage(root), 'success');
    shell?.announce?.(savedMessage(root), 'ready');
    return true;
  }

  async function saveMarketing(root, state, client, shell, form, control) {
    if (typeof client?.updateProfile !== 'function' || typeof client?.getProfile !== 'function') return { unavailable: true };
    const input = marketingInput(control, state.customer);
    if (!input) return true;
    let customer;
    try {
      customer = await mutate(root, state, form, 'marketing', async (signal) => {
        await client.updateProfile({ profile: input, signal });
        return readCustomer(client, signal);
      });
    } catch (error) {
      renderMarketing(root, state.customer);
      throw error;
    }
    if (customer === MUTATION_ABORTED) return true;
    if (!customer) {
      renderMarketing(root, state.customer);
      return true;
    }
    state.customer = Object.assign({}, state.customer || {}, { marketing: customer.marketing });
    state.profileWritable = true;
    renderMarketing(root, state.customer);
    formMessage(root, 'marketing', savedMessage(root), 'success');
    shell?.announce?.(savedMessage(root), 'ready');
    return true;
  }

  async function saveAddress(root, state, client, shell, form) {
    syncAddressNames(form);
    normalizeLocalPhoneControl(form?.elements?.namedItem?.('phone'), root);
    if (!validateForm(root, form, 'address')) return true;
    const addressId = asText(form?.elements?.namedItem?.('addressId')?.value).trim();
    const input = addressInput(form);
    const method = addressId ? 'updateAddress' : 'createAddress';
    if (typeof client?.[method] !== 'function') return { unavailable: true };
    if (typeof client?.listAddresses !== 'function') return { unavailable: true };
    const addresses = await mutate(root, state, form, 'address', async (signal) => {
      if (addressId) await client.updateAddress({ addressId, address: input, signal });
      else await client.createAddress({ address: input, signal });
      return readAddresses(client, signal);
    });
    if (addresses === MUTATION_ABORTED) return true;
    if (!addresses) return true;
    state.addressWritable = true;
    state.addresses = addresses;
    renderAddresses(root, state.addresses);
    configureMutationControls(root, { profileWritable: state.profileWritable, addressWritable: state.addressWritable });
    closeDialog(root, state, true, 'address');
    shell?.announce?.(savedMessage(root), 'ready');
    return true;
  }

  async function deleteAddress(root, state, client, shell) {
    if (typeof client?.deleteAddress !== 'function' || typeof client?.listAddresses !== 'function') return { unavailable: true };
    const addressId = asText(state.pendingDeleteAddress?.id).trim();
    if (!addressId) return true;
    const target = dialog(root, 'delete-address');
    const addresses = await mutate(root, state, target, 'delete-address', async (signal) => {
      await client.deleteAddress({ addressId, signal });
      return readAddresses(client, signal);
    }, 'address');
    if (addresses === MUTATION_ABORTED) return true;
    if (!addresses) return true;
    state.addressWritable = true;
    state.addresses = addresses;
    renderAddresses(root, state.addresses);
    configureMutationControls(root, { profileWritable: state.profileWritable, addressWritable: state.addressWritable });
    closeDialog(root, state, true, 'delete-address');
    shell?.announce?.(savedMessage(root), 'ready');
    return true;
  }

  async function setDefaultAddress(root, state, client, shell, control) {
    if (typeof client?.updateAddress !== 'function' || typeof client?.listAddresses !== 'function') return { unavailable: true };
    const addressId = asText(control?.dataset?.pcAddressId).trim();
    if (!addressId) return true;
    const address = state.addresses.find((item) => sameAddressId(item?.id, addressId));
    if (!address) return true;
    const addresses = await mutate(root, state, control, 'addresses', async (signal) => {
      await client.updateAddress({ addressId, address: defaultAddressInput(address, root), signal });
      const readback = await readAddresses(client, signal);
      const defaults = readback.filter((address) => address?.isDefault === true);
      if (defaults.length !== 1 || !sameAddressId(defaults[0]?.id, addressId)) throw new Error('Default address readback failed.');
      return readback;
    }, 'address');
    if (addresses === MUTATION_ABORTED) return true;
    if (!addresses) return true;
    state.addressWritable = true;
    state.addresses = addresses;
    renderAddresses(root, state.addresses);
    configureMutationControls(root, { profileWritable: state.profileWritable, addressWritable: state.addressWritable });
    formMessage(root, 'addresses', savedMessage(root), 'success');
    shell?.announce?.(savedMessage(root), 'ready');
    return true;
  }

  const profileModule = {
    load: loadProfile,
    render({ root, response, shell }) {
      const profileRoot = viewRoot(root, 'profile');
      const state = getState(profileStates, profileRoot);
      const customer = dataOf(response?.profile);
      const addresses = dataOf(response?.addresses);
      const client = shell?.state?.adapter;
      const partial = response?.partial === true;
      const partialNotice = profileRoot.querySelector('[data-pc-profile-local-partial]');
      state.customer = customer;
      state.addresses = Array.isArray(addresses) ? addresses : [];
      state.profileWritable = profileMutationAvailable(client, response, customer);
      state.addressWritable = addressMutationAvailable(client, response, addresses);
      prepareTerritorySelects(profileRoot);
      renderCustomer(profileRoot, customer);
      renderAddresses(profileRoot, state.addresses);
      configureMutationControls(profileRoot, { profileWritable: state.profileWritable, addressWritable: state.addressWritable });
      installLocalGuards(profileRoot, state);
      if (partialNotice) partialNotice.hidden = !partial;
      return { state: customer == null && addresses == null ? 'empty' : 'ready', partial };
    },
    async handleAction({ event, action, root, client, shell }) {
      const profileRoot = viewRoot(root, 'profile');
      const state = getState(profileStates, profileRoot);
      const control = action || event?.target?.closest?.('[data-pc-action]');
      const name = control?.dataset?.pcAction || control?.dataset?.pcMutationForm;
      if (!name) return false;
      if (name === 'retry-profile') {
        event?.preventDefault();
        return { view: 'profile', params: {} };
      }
      if (name === 'close-dialog') {
        event?.preventDefault();
        closeDialog(profileRoot, state, true);
        return true;
      }
      if ((!state.addressWritable && ['add-address', 'edit-address', 'delete-address', 'set-default-address', 'confirm-delete-address', 'address'].includes(name))
        || (!state.profileWritable && ['profile', 'marketing'].includes(name))) {
        event?.preventDefault();
        return true;
      }
      if (name === 'add-address' || name === 'edit-address') {
        event?.preventDefault();
        return openAddressDialog(profileRoot, state, control);
      }
      if (name === 'delete-address') {
        event?.preventDefault();
        return openDeleteDialog(profileRoot, state, control);
      }
      if (name === 'set-default-address') {
        event?.preventDefault();
        return setDefaultAddress(profileRoot, state, client, shell, control);
      }
      if (name === 'confirm-delete-address') {
        event?.preventDefault();
        return deleteAddress(profileRoot, state, client, shell);
      }
      if (name === 'profile') return saveProfile(profileRoot, state, client, shell, control);
      if (name === 'marketing') return saveMarketing(profileRoot, state, client, shell, control, event?.target);
      if (name === 'address') return saveAddress(profileRoot, state, client, shell, control);
      return false;
    },
    disconnect({ root }) {
      const profileRoot = viewRoot(root, 'profile');
      const state = profileStates.get(profileRoot);
      if (!state) return;
      state.mutationOperations.forEach(({ controller, target }) => {
        controller.abort();
        setBusy(target, false);
      });
      state.mutationOperations.clear();
      closeDialog(profileRoot, state, false);
      clearListeners(state);
      profileStates.delete(profileRoot);
    }
  };

  window.PersonalCenterViewModules = window.PersonalCenterViewModules || {};
  window.PersonalCenterViewModules.profile = profileModule;
  if (typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new window.CustomEvent('personal-center:module-ready', { detail: { views: ['profile'] } }));
  }
})();
